import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm({ t, lang }) {
  const services = t.services_list;
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: services[0],
    moving_date: "",
    from_address: "",
    to_address: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service_type) {
      toast.error(t.form.error);
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/quote`, form);
      if (res.data?.status === "received") {
        setDone(true);
        toast.success(t.form.success);
      } else {
        toast.error(t.form.error);
      }
    } catch {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="bg-black text-white border-b-2 border-black" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold tracking-[0.22em] text-[#E11D2E]">{t.form.eyebrow}</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1]">
            {t.form.title}
          </h2>
          <p className="mt-6 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
            {t.form.subtitle}
          </p>

          <div className="mt-10 space-y-4">
            {[
              { k: "01", v: lang === "no" ? "Fyll ut skjema" : "Fill the form" },
              { k: "02", v: lang === "no" ? "Motta fastpris" : "Receive fixed price" },
              { k: "03", v: lang === "no" ? "Raskt hjem" : "Quickly home" },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-4 border-b border-zinc-800 pb-4" data-testid={`process-step-${i}`}>
                <div className="font-display text-3xl text-[#E11D2E] w-12">{s.k}</div>
                <div className="font-display text-xl">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              className="bg-white text-black p-8 md:p-12 border-2 border-[#E11D2E] brutal-shadow-red"
              data-testid="quote-success-card"
            >
              <CheckCircle2 size={48} className="text-[#E11D2E]" />
              <h3 className="font-display text-3xl md:text-4xl mt-5">{t.form.success}</h3>
              <p className="mt-3 text-zinc-600">
                {lang === "no"
                  ? "Vi har mottatt forespørselen din. Forvent svar innen kort tid."
                  : "We've received your request. Expect a reply shortly."}
              </p>
              <button
                onClick={() => {
                  setDone(false);
                  setForm({ ...form, name: "", phone: "", email: "", from_address: "", to_address: "", message: "" });
                }}
                className="mt-6 bg-black text-white px-6 py-3 border-2 border-black font-bold uppercase tracking-wider text-sm brutal-shadow-red"
                data-testid="quote-send-another"
              >
                {lang === "no" ? "Send ny forespørsel" : "Send another"}
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-white text-black p-6 md:p-8 border-2 border-white"
              data-testid="quote-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.form.name} testid="quote-name">
                  <input
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="input-brutal"
                    data-testid="quote-input-name"
                  />
                </Field>
                <Field label={t.form.phone} testid="quote-phone">
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="input-brutal"
                    data-testid="quote-input-phone"
                  />
                </Field>
                <Field label={t.form.email} testid="quote-email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="input-brutal"
                    data-testid="quote-input-email"
                  />
                </Field>
                <Field label={t.form.service} testid="quote-service">
                  <select
                    value={form.service_type}
                    onChange={update("service_type")}
                    className="input-brutal"
                    data-testid="quote-input-service"
                  >
                    {services.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t.form.date} testid="quote-date">
                  <input
                    type="date"
                    value={form.moving_date}
                    onChange={update("moving_date")}
                    className="input-brutal"
                    data-testid="quote-input-date"
                  />
                </Field>
                <div className="hidden sm:block" />
                <Field label={t.form.from} testid="quote-from">
                  <input
                    value={form.from_address}
                    onChange={update("from_address")}
                    className="input-brutal"
                    data-testid="quote-input-from"
                  />
                </Field>
                <Field label={t.form.to} testid="quote-to">
                  <input
                    value={form.to_address}
                    onChange={update("to_address")}
                    className="input-brutal"
                    data-testid="quote-input-to"
                  />
                </Field>
              </div>
              <Field label={t.form.message} testid="quote-message">
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder={t.form.message_placeholder}
                  className="input-brutal resize-none"
                  data-testid="quote-input-message"
                />
              </Field>

              <button
                type="submit"
                disabled={sending}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#E11D2E] text-white px-6 py-4 font-bold uppercase tracking-wider text-sm border-2 border-black brutal-shadow disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="quote-submit-button"
              >
                {sending ? t.form.sending : <>{t.form.submit} <Send size={16} /></>}
              </button>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .input-brutal {
          width: 100%;
          padding: 12px 14px;
          border: 2px solid #0A0A0A;
          background: #FFFFFF;
          color: #0A0A0A;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 15px;
          outline: none;
          border-radius: 0;
          transition: box-shadow 0.15s ease;
        }
        .input-brutal:focus { box-shadow: 4px 4px 0 0 #E11D2E; border-color: #E11D2E; }
      `}</style>
    </section>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block mt-4 first:mt-0" data-testid={`${testid}-field`}>
      <span className="block text-[11px] font-bold tracking-[0.22em] uppercase text-zinc-500 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
